import type { RowAction } from '@/components/ui/data-table/types';
import type { Workorder } from '@certhon/models';
import { Phone, MapPin, PenLine, CheckCircle, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { completeWorkorder } from './workorders.api';

export { adaptActionsForPanel } from '@/utils/adaptActionsForPanel';

/**
 * Open Google Maps with the location address as destination
 */
function openInGoogleMaps(address: string) {
  const encodedAddress = encodeURIComponent(address);
  const url = `https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}`;
  window.open(url, '_blank');
}

/**
 * Initiate a phone call
 */
function callPhoneNumber(phone: string) {
  window.location.href = `tel:${phone}`;
}

/**
 * Open workorder PDF in a new browser tab
 */
function openWorkorderPdf(recnum: number) {
  const url = `/api/workorders/${recnum}/pdf`;
  window.open(url, '_blank');
}

export interface WorkorderActionHandlers {
  onNavigateToSign: () => void;
  onCompleteWithoutSignature: () => Promise<void>;
}

/**
 * Get actions for a workorder - works for both row actions (table) and panel actions
 *
 * @param handlers - Optional handlers for navigation and completion actions
 * @param workorder - Optional workorder instance for filtering actions (e.g., hide completion actions for completed workorders)
 * @returns Array of row actions that can be adapted for panels using adaptActionsForPanel
 */
export function getWorkorderActions(
  handlers?: WorkorderActionHandlers,
  workorder?: Workorder,
  options: { canEdit?: boolean } = {},
): RowAction<Workorder>[] {
  const isCompleted = workorder?.completed ?? false;
  const canEdit = options.canEdit ?? true;
  const actions: RowAction<Workorder>[] = [
    // Call contact action
    {
      id: 'call-contact',
      icon: Phone,
      label: 'Call contact',
      sublabel: (wo) => wo.contact_name || null,
      disabled: (wo) => !wo.contact_phone,
      onCalled: (wo) => {
        if (wo.contact_phone) {
          callPhoneNumber(wo.contact_phone);
        } else {
          toast.error('No contact phone number available');
        }
      },
    },

    // Call office action
    {
      id: 'call-office',
      icon: Phone,
      label: 'Call office',
      sublabel: (wo) => wo.customer_name || null,
      disabled: (wo) => !wo.customer_phone,
      onCalled: (wo) => {
        if (wo.customer_phone) {
          callPhoneNumber(wo.customer_phone);
        } else {
          toast.error('No office phone number available');
        }
      },
    },

    // Open location in Google Maps
    {
      id: 'open-in-maps',
      icon: MapPin,
      label: 'Open in Maps',
      disabled: (wo) => !wo.location_address,
      onCalled: (wo) => {
        if (wo.location_address) {
          openInGoogleMaps(wo.location_address);
        } else {
          toast.error('No address available');
        }
      },
    },
  ];

  // Only show completion actions if the workorder is still open and the
  // current user is allowed to work on it
  if (!isCompleted && canEdit) {
    actions.push(
      // Sign and complete action
      {
        id: 'sign-and-complete',
        icon: PenLine,
        label: 'Sign & Complete',
        onCalled: () => {
          if (handlers?.onNavigateToSign) {
            handlers.onNavigateToSign();
          } else {
            toast.info('Signature flow not available');
          }
        },
      },
      // Complete without signature action
      {
        id: 'complete-without-signature',
        icon: CheckCircle,
        label: 'Complete without signature',
        onCalled: async () => {
          if (handlers?.onCompleteWithoutSignature) {
            await handlers.onCompleteWithoutSignature();
          } else {
            toast.info('Complete without signature not available');
          }
        },
      },
    );
  }

  // View PDF action - only show for completed workorders
  if (isCompleted) {
    actions.push({
      id: 'view-report-pdf',
      icon: FileText,
      label: 'View report PDF ',
      onCalled: (wo) => {
        openWorkorderPdf(wo.recnum);
      },
    });
  }

  return actions;
}

/**
 * Complete a workorder without requiring a signature.
 * Uses the dedicated complete endpoint which handles prematerial copying,
 * pdf_task generation, and other backend logic.
 */
export async function completeWorkorderWithoutSignature(
  recnum: number,
): Promise<void> {
  await completeWorkorder(recnum);
}
